import React from "react";
import {
  IFrequentlyAskedQuestionsProps,
  IFAQSchemaMainEntityProps,
} from "./FrequentlyAskedQuestionsProps";
import {
  StyledListGroup,
  QuestionsTab,
} from "./FrequentlyAskedQuestionsHelpers";
import { useLocation } from "@reach/router";
import FAQAutocomplete from "./FAQAutocomplete/FAQAutocomplete";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Tab from "react-bootstrap/Tab";
import ListGroup from "react-bootstrap/ListGroup";
import SubTitle from "../../global/SubTitle/SubTitle";
import Title2 from "../../global/Title2/Title2";
import Title3 from "../../global/Title3/Title3";
import { Helmet } from "react-helmet";
import { useMobileDetectContext } from "../../../context/MobileDetectContext";

const FrequentlyAskedQuestions: React.FC<IFrequentlyAskedQuestionsProps> = (
  props
) => {
  // return null;
  const node = props.node;
  const sections = node?.relationships?.faq_sections || null;
  const location = useLocation();
  const { isMobileOnly } = useMobileDetectContext();
  const formatId = (str: string) => str.replace(/ /g, "");
  let isSupportpage = false;
  if (location && location.pathname.indexOf("/support") != -1) {
    isSupportpage = true;
  }
  const [activeSectionKey, setActiveSectionKey] = React.useState(
    sections && sections[0] ? formatId(sections[0].field_title) : ""
  );
  const [activeQuesKey, setActiveQuesKey] = React.useState("");
  const isFAQVisible = props.node.field_search_field_required;

  const {
    activeSectionKey: propActiveSection,
    activeQuestionKey: propActiveQuestion,
    afterTileClicked: propAfterTileClicked,
  } = props;

  React.useEffect(() => {
    if (propActiveSection) {
      setActiveSectionKey(propActiveSection);
      setActiveQuesKey(propActiveQuestion);
      propAfterTileClicked && propAfterTileClicked();
    }
  }, [propActiveSection, propActiveQuestion]);

  const allQuestions: any = [];
  sections.forEach((section) => {
    section.relationships.field_questions.forEach((q) => {
      allQuestions.push({
        question: q?.question,
        quesId: q?.question,
        sectionId: section?.field_title,
        answer: q?.answer?.processed,
      });
    });
  });

  const handleFAQSearch = (value: any) => {
    value = value.trim();
    const questionsFound = allQuestions.filter((q: any) => {
      return q.question.toLowerCase().indexOf(value.toLowerCase()) != -1;
    });
    return questionsFound;
  };

  const handleFAQSearchSelection = (selectedFaq: any) => {
    setActiveSectionKey(formatId(selectedFaq.sectionId));
    setActiveQuesKey(selectedFaq.quesId);
  };

  const handleFAQSectionClick = (e: any) => {
    const sectionId = e.target.dataset["id"];
    setActiveSectionKey(sectionId);
  };

  const handleQuestionClick = (key: any) => {
    // if question clicked is current active question then update activeQuesKey with blank to make it collapse.
    const newActiveKey = key == activeQuesKey ? "" : key;
    setActiveQuesKey(newActiveKey);
  };

  const FAQSchema = () => {
    const mainEntity: IFAQSchemaMainEntityProps[] = [];
    allQuestions.map((q: { question: string; answer: string }) => {
      mainEntity.push({
        "@type": "Question",
        name: q.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: q.answer,
        },
      });
    });
    const schemaJson = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: mainEntity,
    };
    return allQuestions?.length >= 1 ? (
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schemaJson)}</script>
      </Helmet>
    ) : null;
  };

  return (
    <>
      <Container fluid className="bg-light pt-5 pb-5 mb-5">
        <FAQSchema />
        <Container fluid>
          <Row>
            <Col className="text-left">
              <SubTitle>{node.field_subtitle}</SubTitle>
            </Col>
          </Row>
          <Row>
            <Col lg={4} className="text-left">
              {isSupportpage ? (
                <Title3 as="h2"> {node.field_title}</Title3>
              ) : (
                <Title2>{node.field_title}</Title2>
              )}
            </Col>
            <Col lg={{ offset: 1 }}>
              <div
                className="description"
                dangerouslySetInnerHTML={{ __html: node.field_description }}
              />
            </Col>
          </Row>
          <Row className="mt-3 mt-sm-5">
            <Col className="justify-content-between">
              <Tab.Container
                defaultActiveKey={`${sections[0].field_title}`}
                activeKey={activeSectionKey}
              >
                <Row>
                  <Col lg={4}>
                    {isFAQVisible ? (
                      <>
                        <span>
                          <small id="how-may-we-help">How May We Help?</small>
                        </span>
                        <FAQAutocomplete
                          onFAQSearchRequested={handleFAQSearch}
                          onFAQSelection={handleFAQSearchSelection}
                          placeholder="Start typing"
                          id={"faq-auto-complete"}
                        />
                      </>
                    ) : null}
                    <StyledListGroup className="mt-3">
                      {sections.map((section, idx) => {
                        const questions =
                          section.relationships.field_questions || null;
                        return (
                          <div key={`RF-${idx}`} id={`faq-category-${idx + 1}`}>
                            <ListGroup.Item
                              as="button"
                              active={
                                activeSectionKey ==
                                `${formatId(section.field_title)}`
                              }
                              onClick={handleFAQSectionClick}
                              data-id={formatId(section.field_title)}
                              id={`support-faq-${formatId(
                                section.field_title
                              )}`}
                            >
                              {section.field_title}
                            </ListGroup.Item>
                            {/* Questions for Mobile view only as in mobile view these are below category names */}
                            {isMobileOnly ? (
                              <QuestionsTab
                                preFixer="m"
                                className="d-block d-lg-none"
                                section={section}
                                questions={questions}
                                activeQuesKey={activeQuesKey}
                                onQuesToggle={handleQuestionClick}
                                id={idx + 1}
                              />
                            ) : null}
                          </div>
                        );
                      })}
                    </StyledListGroup>
                  </Col>
                  {/* Questions for desktop view only - in right hand section */}
                  <Col
                    lg={{ span: 7, offset: 1 }}
                    className="d-none d-lg-block"
                  >
                    {sections.map((section, secIndex) => {
                      const questions =
                        section.relationships.field_questions || null;
                      return (
                        <>
                          {!isMobileOnly ? (
                            <QuestionsTab
                              section={section}
                              questions={questions}
                              key={`qt-${secIndex}`}
                              activeQuesKey={activeQuesKey}
                              onQuesToggle={handleQuestionClick}
                              className="d-none d-lg-block"
                              id={secIndex + 1}
                            />
                          ) : null}
                        </>
                      );
                    })}
                  </Col>
                </Row>
              </Tab.Container>
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
};

export default FrequentlyAskedQuestions;
